import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Users, Plus, Mail, Send, BookOpen } from "lucide-react";

export function MessagesZone({
  user,
  conversations,
  activeConversation,
  conversationMessages,
  newMessage,
  setNewMessage,
  fetchMessages,
  handleSendMessage,
  setShowNewMessage
}) {
  return (
    <div className="space-y-6">
      {/* How-to Instructions Card */}
      <Card className="bg-gradient-to-r from-purple-500/10 to-neutral-900/50 border-purple-500/20">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <BookOpen className="w-5 h-5 text-purple-400" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-white mb-2">How to Use Direct Messages</h3>
              <ul className="text-sm text-neutral-300 space-y-2">
                <li className="flex items-start gap-2">
                  <span className="text-purple-400 font-bold">1.</span>
                  <span><strong>Start a new conversation</strong> - Click the + button to message any community member</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-400 font-bold">2.</span>
                  <span><strong>Introduce yourself first</strong> - Briefly explain who you are and why you're reaching out</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-400 font-bold">3.</span>
                  <span><strong>Be specific about your interest</strong> - Mention what attracted you to connect with them</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-400 font-bold">4.</span>
                  <span><strong>Propose next steps</strong> - Suggest a call, collaboration, or specific ask</span>
                </li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-3 gap-6 h-[600px]">
        {/* Conversations List */}
        <div className="lg:col-span-1 bg-neutral-800/50 rounded-lg border border-neutral-700 flex flex-col">
          <div className="p-4 border-b border-neutral-700 flex items-center justify-between">
            <h2 className="font-bold text-white flex items-center gap-2">
              <Mail className="w-5 h-5 text-[#39FF14]" />
              Messages
            </h2>
            <Button
              size="sm"
              onClick={() => setShowNewMessage(true)}
              className="bg-[#39FF14] text-black hover:bg-[#32e612]"
              data-testid="new-message-btn"
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>

          <div className="flex-1 overflow-y-auto">
            {conversations.length > 0 ? conversations.map((conv) => (
              <div
                key={conv.user_id}
                onClick={() => fetchMessages(conv.user_id)}
                className={`p-4 border-b border-neutral-700/50 cursor-pointer hover:bg-neutral-700/30 transition-colors ${
                  activeConversation?.user_id === conv.user_id ? "bg-neutral-700/50" : ""
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <div className="w-10 h-10 bg-[#39FF14]/10 rounded-full flex items-center justify-center">
                      {conv.picture ? (
                        <img src={conv.picture} alt="" className="w-10 h-10 rounded-full" />
                      ) : (
                        <Users className="w-5 h-5 text-[#39FF14]" />
                      )}
                    </div>
                    {conv.unread_count > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                        {conv.unread_count}
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-white truncate">{conv.name}</p>
                    <p className="text-sm text-neutral-400 truncate">
                      {conv.is_from_me && <span className="text-neutral-500">You: </span>}
                      {conv.last_message}
                    </p>
                  </div>
                  <span className="text-xs text-neutral-500">
                    {conv.last_message_time ? new Date(conv.last_message_time).toLocaleDateString() : ""}
                  </span>
                </div>
              </div>
            )) : (
              <div className="p-8 text-center">
                <Mail className="w-12 h-12 mx-auto mb-3 text-neutral-600" />
                <p className="text-neutral-400">No conversations yet</p>
                <Button
                  onClick={() => setShowNewMessage(true)}
                  className="mt-4 bg-[#39FF14] text-black hover:bg-[#32e612]"
                >
                  Start a Conversation
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Conversation View */}
        <div className="lg:col-span-2 bg-neutral-800/50 rounded-lg border border-neutral-700 flex flex-col">
          {activeConversation ? (
            <>
              {/* Conversation Header */}
              <div className="p-4 border-b border-neutral-700 flex items-center gap-3">
                <div className="w-10 h-10 bg-[#39FF14]/10 rounded-full flex items-center justify-center">
                  {activeConversation.picture ? (
                    <img src={activeConversation.picture} alt="" className="w-10 h-10 rounded-full" />
                  ) : (
                    <Users className="w-5 h-5 text-[#39FF14]" />
                  )}
                </div>
                <div className="flex-1">
                  <p className="font-medium text-white">{activeConversation.name}</p>
                  <p className="text-sm text-neutral-400 truncate">{activeConversation.business_description || "Community Member"}</p>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {conversationMessages.map((msg) => {
                  const isMe = msg.sender_id === user?.user_id;
                  return (
                    <div key={msg.message_id} className={`flex ${isMe ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[70%] p-3 rounded-lg ${
                        isMe
                          ? "bg-[#39FF14] text-black"
                          : "bg-neutral-700 text-white"
                      }`}>
                        <p className="whitespace-pre-wrap">{msg.content}</p>
                        <p className={`text-xs mt-1 ${isMe ? "text-black/60" : "text-neutral-400"}`}>
                          {msg.created_at ? new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ""}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Message Input */}
              <div className="p-4 border-t border-neutral-700">
                <div className="flex gap-2">
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type a message..."
                    className="bg-neutral-900 border-neutral-700 flex-1"
                    onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && handleSendMessage()}
                    data-testid="message-input"
                  />
                  <Button
                    onClick={handleSendMessage}
                    className="bg-[#39FF14] text-black hover:bg-[#32e612]"
                    disabled={!newMessage.trim()}
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <Mail className="w-16 h-16 mx-auto mb-4 text-neutral-600" />
                <p className="text-neutral-400 mb-2">Select a conversation or start a new one</p>
                <Button
                  onClick={() => setShowNewMessage(true)}
                  className="bg-[#39FF14] text-black hover:bg-[#32e612]"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  New Message
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
